import React from "react";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Container, Grid2 as Grid, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";
import { useTheme } from "@emotion/react";
import { ProductThumbnail } from "src/components/ProductImages";

const useStyles = makeStyles()(theme => ({
    root: {
        paddingTop: 200,
        paddingBottom: 200,
        [theme.breakpoints.down("xl")]: {
            paddingTop: 60,
            paddingBottom: 60,
        },
    },
    featureCard: {
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        borderRadius: theme.spacing(2),
        padding: theme.spacing(3),
        textAlign: "center",
        backdropFilter: "blur(10px)",
    },
    ctaButton: {
        padding: "12px 40px",
        fontSize: "1.1rem",
    },
}));

const Hero = ({ className, ...rest }) => {
    const { t } = useTranslation();
    const { classes, cx } = useStyles();
    const theme = useTheme();
    const smallMobileDevice = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <div className={cx(classes.root, className)} {...rest}>
            <Container maxWidth="lg">
                <Grid container spacing={3} style={{ marginTop: "36px" }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box
                            display="flex"
                            flexDirection="column"
                            justifyContent="center"
                            height="100%"
                        >
                            <Typography variant="h1" color="textPrimary">
                                {t("heroTitle")}
                            </Typography>
                            <Box mt={3}>
                                <Typography variant="h5" color="textPrimary">
                                    {t("heroSubtitle")}
                                </Typography>
                            </Box>
                            <Box mt={4} display="flex" gap={2}>
                                <Button
                                    color="secondary"
                                    variant="contained"
                                    className={classes.ctaButton}
                                >
                                    {t("products")}
                                </Button>
                                <Button
                                    color="primary"
                                    variant="outlined"
                                    className={classes.ctaButton}
                                    sx={{ borderColor: "white", color: "white" }}
                                >
                                    {t("aboutUs")}
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box mt={smallMobileDevice ? 4 : 0}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6 }}>
                                    <Box className={classes.featureCard}>
                                        <Typography variant="h3" color="secondary">
                                            100%
                                        </Typography>
                                        <Typography variant="body1" color="textPrimary">
                                            Organic Ingredients
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Box className={classes.featureCard}>
                                        <Typography variant="h3" color="secondary">
                                            14+
                                        </Typography>
                                        <Typography variant="body1" color="textPrimary">
                                            Days Fermented
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Box className={classes.featureCard}>
                                        <Typography variant="h3" color="secondary">
                                            6
                                        </Typography>
                                        <Typography variant="body1" color="textPrimary">
                                            Unique Flavors
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Box className={classes.featureCard}>
                                        <Typography variant="h3" color="secondary">
                                            1B+
                                        </Typography>
                                        <Typography variant="body1" color="textPrimary">
                                            Probiotics per Bottle
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>

                {/* Health Benefits Section */}
                <Box mt={10}>
                    <Typography variant="h2" color="textPrimary" align="center" gutterBottom>
                        {t("healthBenefits")}
                    </Typography>
                    <Grid container spacing={3} mt={4}>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Box className={classes.featureCard}>
                                <Typography variant="h5" color="secondary" gutterBottom>
                                    {t("probiotics")}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Our kombucha contains billions of beneficial bacteria to support gut health.
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Box className={classes.featureCard}>
                                <Typography variant="h5" color="secondary" gutterBottom>
                                    {t("antioxidants")}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Loaded with antioxidants from premium green and black tea blends.
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Box className={classes.featureCard}>
                                <Typography variant="h5" color="secondary" gutterBottom>
                                    {t("energy")}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Natural energy from B vitamins and organic acids without the crash.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Featured Products Section */}
                <Box mt={10}>
                    <Typography variant="h2" color="textPrimary" align="center" gutterBottom>
                        {t("featuredProducts")}
                    </Typography>
                    <Grid container spacing={3} mt={4}>
                        {[
                            { slug: "original", name: t("originalKombucha"), desc: "Classic, crisp, and refreshing" },
                            { slug: "ginger-lemon", name: t("gingerLemon"), desc: "Zesty with a kick of ginger" },
                            { slug: "berry-blast", name: t("berryBlast"), desc: "Sweet and tangy berry medley" },
                            { slug: "tropical-mango", name: t("tropicalMango"), desc: "Exotic tropical paradise" },
                            { slug: "lavender-honey", name: t("lavenderHoney"), desc: "Calming floral notes" },
                            { slug: "mint-mojito", name: t("mintMojito"), desc: "Cool and invigorating" },
                        ].map((product, index) => (
                            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                                <Box
                                    className={classes.featureCard}
                                    component={RouterLink}
                                    to={`/products/${product.slug}`}
                                    sx={{
                                        textDecoration: "none",
                                        display: "block",
                                        transition: "transform 0.2s",
                                        "&:hover": { transform: "translateY(-4px)" }
                                    }}
                                >
                                    <Box display="flex" justifyContent="center" mb={2}>
                                        <ProductThumbnail productId={product.slug} />
                                    </Box>
                                    <Typography variant="h5" color="primary" gutterBottom align="center">
                                        {product.name}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" align="center">
                                        {product.desc}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Container>
        </div>
    );
};

Hero.propTypes = {
    className: PropTypes.string,
};

export default Hero;
